/**
 * Brand Book Service
 * 
 * Generates comprehensive brand books from website URLs.
 * Stores them in Firestore for use by other AI services.
 */

import { db } from '../config/firebase';
import { industryTemplateService } from './industryTemplateService';
import { JourneyMetdata } from '../types/marketingStrategy';

/**
 * Recursively removes undefined values from an object
 * Firestore doesn't accept undefined values
 */
function removeUndefinedValues<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedValues(item)) as T;
  }
  
  if (typeof obj === 'object' && !(obj instanceof Date) && !(obj instanceof Timestamp)) {
    const cleaned: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefinedValues(value);
      }
    }
    return cleaned as T;
  }
  
  return obj;
}
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy, 
  Timestamp,
  limit
} from 'firebase/firestore';
import { GoogleGenAI } from '@google/genai';
import { firecrawlService } from './firecrawlService';
import { geminiService } from './geminiService';
import { pageSpeedService } from './pageSpeedService';
import { customerIntelligenceService } from './customerIntelligenceService';
import type { 
  BrandBook, 
  BrandBookListItem, 
  BrandBookProgress,
  ImageGenContext,
  AdCopyContext,
  BrandContext,
  VoiceAnalysis,
  ImageStyle
} from '../types/brandBook';

function normalizeBrandBookDates(id: string, data: any): BrandBook {
  const createdAt = data.createdAt?.toDate?.() || new Date();
  const updatedAt = data.updatedAt?.toDate?.() || new Date();
  const analyzedAtRaw = data.pageSpeed?.analyzedAt;
  const analyzedAt = analyzedAtRaw?.toDate?.() || analyzedAtRaw || undefined;
  const guidelinesCreatedAtRaw = data.guidelines?.createdAt;
  const guidelinesCreatedAt = guidelinesCreatedAtRaw?.toDate?.() || guidelinesCreatedAtRaw || undefined;

  return {
    ...data,
    id,
    brandColors: Array.isArray(data.brandColors) ? data.brandColors : [],
    createdAt,
    updatedAt,
    pageSpeed: data.pageSpeed
      ? {
          ...data.pageSpeed,
          analyzedAt
        }
      : undefined,
    guidelines: data.guidelines
      ? {
          ...data.guidelines,
          createdAt: guidelinesCreatedAt
        }
      : undefined
  } as BrandBook;
}

/**
 * Extract domain from URL
 */
function extractDomain(url: string): string {
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
    return urlObj.hostname.replace('www.', '');
  } catch {
    return url.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0];
  }
}

/**
 * Analyze brand colors from scraped images and content
 */
async function extractBrandColors(html: string, images: string[]): Promise<string[]> {
  const colors: string[] = [];
  
  // Extract colors from inline styles
  const colorRegex = /#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})\b/g;
  const matches = html.match(colorRegex) || [];
  
  // Count occurrences and get most common
  const colorCounts: Record<string, number> = {};
  matches.forEach(color => {
    const normalized = color.toLowerCase();
    colorCounts[normalized] = (colorCounts[normalized] || 0) + 1;
  });
  
  // Sort by count and take top 5
  const sortedColors = Object.entries(colorCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([color]) => color);
  
  colors.push(...sortedColors);
  
  // Fallback if no colors found
  if (colors.length === 0) {
    colors.push('#2563eb', '#1e40af'); // Default blue
  }
  
  return colors;
}

/**
 * Analyze image style from scraped images
 */
function analyzeImageStyle(images: string[], markdown: string): ImageStyle {
  const imgLower = images.map(i => i.toLowerCase()).join(' ');
  const contentLower = markdown.toLowerCase();
  
  // Detect photography style
  let photography = 'corporate';
  if (imgLower.includes('lifestyle') || contentLower.includes('lifestyle')) {
    photography = 'lifestyle';
  } else if (imgLower.includes('product') || imgLower.includes('produkt')) {
    photography = 'produkt';
  } else if (imgLower.includes('team') || imgLower.includes('office')) {
    photography = 'corporate';
  }
  
  // Detect color mood
  let colorMood = 'neutral';
  if (contentLower.includes('warm') || contentLower.includes('freundlich')) {
    colorMood = 'warm';
  } else if (contentLower.includes('kühl') || contentLower.includes('modern')) {
    colorMood = 'kühl';
  }
  
  // Detect subjects
  const subjects: string[] = [];
  if (contentLower.includes('team') || contentLower.includes('mitarbeiter')) {
    subjects.push('Menschen');
  }
  if (contentLower.includes('produkt') || contentLower.includes('product')) {
    subjects.push('Produkte');
  }
  if (contentLower.includes('büro') || contentLower.includes('office') || contentLower.includes('gebäude')) {
    subjects.push('Architektur');
  }
  if (subjects.length === 0) {
    subjects.push('Allgemein');
  }
  
  return { photography, colorMood, subjects };
}

function extractLogoFromScrapedImages(images: string[]): string | null {
  if (!Array.isArray(images) || images.length === 0) {
    return null;
  }
  const logoCandidate = images.find((img) => {
    const lower = img.toLowerCase();
    return lower.includes('logo') || lower.includes('brand') || lower.includes('mark');
  });
  return logoCandidate || null;
}

/**
 * Enhanced voice analysis using Gemini
 */
async function analyzeVoiceWithAI(markdown: string, html: string): Promise<VoiceAnalysis> {
  try {
    // First, use the existing tonality detection
    const basicTonality = firecrawlService.detectTonality(markdown, html);
    
    // Then enhance with Gemini analysis
    const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
    
    if (!apiKey) {
      // Return basic analysis without AI enhancement
      return {
        addressForm: basicTonality.addressForm,
        formality: basicTonality.formality,
        confidence: basicTonality.confidence,
        toneAttributes: [basicTonality.formality === 'formal' ? 'professionell' : 'locker'],
        writingStyle: 'sachlich',
        sentenceStructure: 'variiert',
        vocabularyLevel: 'allgemeinverständlich',
        examplePhrases: [],
        keyMessages: [],
        doStatements: [basicTonality.recommendation],
        dontStatements: []
      };
    }
    
    const genAI = new GoogleGenAI({ apiKey });
    
    const response = await genAI.models.generateContent({
      model: 'gemini-2.0-flash',
      contents: [{
        parts: [{
          text: `Analysiere die Tonalität und den Schreibstil dieser Website-Texte für ein Brand Book.

WEBSITE-INHALTE:
${markdown.substring(0, 20000)}

Antworte NUR mit einem JSON-Objekt (keine Markdown-Formatierung):
{
  "toneAttributes": ["attribut1", "attribut2", "attribut3"],
  "writingStyle": "sachlich|emotional|technisch|inspirierend",
  "sentenceStructure": "kurz & prägnant|komplex|variiert",
  "vocabularyLevel": "fachsprachlich|allgemeinverständlich|einfach",
  "examplePhrases": ["typische Formulierung 1", "typische Formulierung 2"],
  "keyMessages": ["Kernbotschaft 1", "Kernbotschaft 2"],
  "doStatements": ["Verwende...", "Nutze..."],
  "dontStatements": ["Vermeide...", "Nicht verwenden..."]
}`
        }]
      }]
    });
    
    const text = response.text || '';
    // Extract JSON from response
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      return {
        addressForm: basicTonality.addressForm,
        formality: basicTonality.formality,
        confidence: basicTonality.confidence,
        toneAttributes: parsed.toneAttributes || [],
        writingStyle: parsed.writingStyle || 'sachlich',
        sentenceStructure: parsed.sentenceStructure || 'variiert',
        vocabularyLevel: parsed.vocabularyLevel || 'allgemeinverständlich',
        examplePhrases: parsed.examplePhrases || [],
        keyMessages: parsed.keyMessages || [],
        doStatements: parsed.doStatements || [],
        dontStatements: parsed.dontStatements || []
      };
    }
    
    throw new Error('Could not parse AI response');
    
  } catch (error) {
    console.warn('Voice AI analysis failed, using basic analysis:', error);
    const basicTonality = firecrawlService.detectTonality(markdown, html);
    
    return {
      addressForm: basicTonality.addressForm,
      formality: basicTonality.formality,
      confidence: basicTonality.confidence,
      toneAttributes: [basicTonality.formality === 'formal' ? 'professionell' : 'locker'],
      writingStyle: 'sachlich',
      sentenceStructure: 'variiert',
      vocabularyLevel: 'allgemeinverständlich',
      examplePhrases: [],
      keyMessages: [],
      doStatements: [basicTonality.recommendation],
      dontStatements: []
    };
  }
}

export const brandBookService = {
  /**
   * Generate a complete brand book from a website URL
   */
  async generateBrandBook(
    url: string, 
    userId: string,
    onProgress?: (progress: BrandBookProgress) => void
  ): Promise<BrandBook> {
    const normalizedUrl = url.startsWith('http') ? url : `https://${url}`;
    const domain = extractDomain(normalizedUrl);
    
    try {
      // Step 1: Scrape website
      onProgress?.({
        status: 'scraping',
        step: 1,
        totalSteps: 4,
        message: 'Website wird gescrapt...'
      });
      
      console.log('🔥 Step 1: Scraping website with Firecrawl...');
      const scrapedData = await firecrawlService.scrapeWebsite(normalizedUrl);
      console.log(`✅ Scraped ${scrapedData.markdown.length} chars`);
      
      // Step 2: Analyze with AI
      onProgress?.({
        status: 'analyzing',
        step: 2,
        totalSteps: 4,
        message: 'Inhalte werden analysiert...'
      });
      
      console.log('🤖 Step 2: Analyzing content with Gemini...');
      const geminiAnalysis = await geminiService.analyzeScrapedContent(
        scrapedData.markdown,
        scrapedData.images,
        scrapedData.metadata,
        normalizedUrl
      );
      
      // Step 3: Enhanced voice analysis
      onProgress?.({
        status: 'analyzing',
        step: 3,
        totalSteps: 4,
        message: 'Brand Voice wird analysiert...'
      });
      
      console.log('🎯 Step 3: Analyzing voice and tone...');
      const voiceAnalysis = await analyzeVoiceWithAI(scrapedData.markdown, scrapedData.html);
      
      // Step 4: Extract brand colors and visual style
      console.log('🎨 Step 4: Extracting brand colors & logo (Firecrawl v2.7.0)...');
      const firecrawlBranding = await firecrawlService.extractBranding(normalizedUrl);
      
      const brandColors = firecrawlBranding?.primaryColor 
        ? [firecrawlBranding.primaryColor, firecrawlBranding.secondaryColor].filter(Boolean) as string[]
        : await extractBrandColors(scrapedData.html, scrapedData.images);
        
      const imageStyle = analyzeImageStyle(scrapedData.images, scrapedData.markdown);
      
      // Extract logo without triggering a second full scrape
      const logoUrl = firecrawlBranding?.logoUrl
        || extractLogoFromScrapedImages(scrapedData.images)
        || `https://logo.clearbit.com/${domain}`;
      
      // Step 5: PageSpeed Analysis (run in background, don't block)
      console.log('📊 Step 5: Running PageSpeed analysis...');
      onProgress?.({
        status: 'analyzing',
        step: 5,
        totalSteps: 5,
        message: 'Analysiere Website-Performance...'
      });
      
      let pageSpeedData: BrandBook['pageSpeed'] = undefined;
      try {
        if (pageSpeedService.isConfigured()) {
          const pageSpeedResult = await pageSpeedService.analyzeFullAsync(normalizedUrl);
          if (pageSpeedResult.mobile || pageSpeedResult.desktop) {
            pageSpeedData = {
              mobile: pageSpeedResult.mobile ? {
                performanceScore: pageSpeedResult.mobile.performanceScore,
                accessibilityScore: pageSpeedResult.mobile.accessibilityScore,
                bestPracticesScore: pageSpeedResult.mobile.bestPracticesScore,
                seoScore: pageSpeedResult.mobile.seoScore,
                overallCategory: pageSpeedResult.mobile.overallCategory,
                firstContentfulPaint: pageSpeedResult.mobile.firstContentfulPaint,
                largestContentfulPaint: pageSpeedResult.mobile.largestContentfulPaint,
                cumulativeLayoutShift: pageSpeedResult.mobile.cumulativeLayoutShift,
                opportunities: pageSpeedResult.mobile.opportunities.slice(0, 3).map(o => ({
                  title: o.title,
                  savings: o.savings
                }))
              } : undefined,
              desktop: pageSpeedResult.desktop ? {
                performanceScore: pageSpeedResult.desktop.performanceScore,
                accessibilityScore: pageSpeedResult.desktop.accessibilityScore,
                bestPracticesScore: pageSpeedResult.desktop.bestPracticesScore,
                seoScore: pageSpeedResult.desktop.seoScore,
                overallCategory: pageSpeedResult.desktop.overallCategory,
                firstContentfulPaint: pageSpeedResult.desktop.firstContentfulPaint,
                largestContentfulPaint: pageSpeedResult.desktop.largestContentfulPaint,
                cumulativeLayoutShift: pageSpeedResult.desktop.cumulativeLayoutShift,
                opportunities: pageSpeedResult.desktop.opportunities.slice(0, 3).map(o => ({
                  title: o.title,
                  savings: o.savings
                }))
              } : undefined,
              analyzedAt: new Date()
            };
            console.log('✅ PageSpeed analysis complete');
          }
        } else {
          console.log('⚠️ PageSpeed API not configured, skipping');
        }
      } catch (pageSpeedError) {
        console.warn('PageSpeed analysis failed (non-blocking):', pageSpeedError);
      }
      
      // Build complete brand book
      onProgress?.({
        status: 'complete',
        step: 5,
        totalSteps: 5,
        message: 'Brand Book erstellt!'
      });
      
      const brandBook: BrandBook = {
        // Meta
        id: '', // Will be set when saved
        url: normalizedUrl,
        domain,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: userId,
        
        // Identity
        businessName: firecrawlBranding?.brandName || geminiAnalysis.businessName || geminiAnalysis.title || domain,
        industry: geminiAnalysis.industry || 'Unbekannt',
        industryKeywords: geminiAnalysis.industryKeywords || [],
        businessModel: geminiAnalysis.businessContext?.businessModel || 'unknown',
        
        // Visual
        logoUrl: logoUrl || undefined,
        brandColors,
        primaryColor: brandColors[0],
        secondaryColor: brandColors[1],
        visualStyle: geminiAnalysis.visualStyle || 'modern',
        imageStyle,
        brandImages: scrapedData.images.slice(0, 10),
        
        // Voice
        voiceAnalysis,
        
        // Messaging
        usp: geminiAnalysis.businessContext?.usp || geminiAnalysis.valueProposition || '',
        slogan: geminiAnalysis.businessContext?.slogan,
        keyBenefits: geminiAnalysis.keyBenefits || [],
        offerings: geminiAnalysis.businessContext?.offerings || geminiAnalysis.features || [],
        targetAudience: geminiAnalysis.targetAudience || '',
        targetAudienceDetails: {
          demographics: geminiAnalysis.targetAudienceDetails?.demographics || '',
          painPoints: geminiAnalysis.targetAudienceDetails?.painPoints || [],
          motivations: geminiAnalysis.targetAudienceDetails?.motivations || []
        },
        primaryCTA: geminiAnalysis.callToAction || 'Kontakt aufnehmen',
        secondaryCTAs: geminiAnalysis.secondaryCTAs || [],
        
        // Keywords
        coreKeywords: geminiAnalysis.suggestedKeywords?.slice(0, 10) || [],
        longTailKeywords: geminiAnalysis.longTailKeywords || [],
        negativeKeywords: geminiAnalysis.negativeKeywords || [],
        
        // Competition
        differentiators: geminiAnalysis.competitorContext?.differentiators || [],
        competitiveAdvantages: geminiAnalysis.competitorContext?.competitiveAdvantages || [],
        avoidPhrases: geminiAnalysis.competitorContext?.avoidPhrases || [],
        
        // Localization
        primaryLanguage: scrapedData.language || 'de',
        country: geminiAnalysis.businessInfo?.country,
        city: geminiAnalysis.businessInfo?.city,
        
        // Performance (PageSpeed)
        pageSpeed: pageSpeedData,
        journeyMetadata: await this.getJourneyMetadataForIndustry(geminiAnalysis.industry)
      };
      
      console.log('✅ Brand Book generated successfully');
      return brandBook;
      
    } catch (error) {
      console.error('❌ Brand Book generation failed:', error);
      onProgress?.({
        status: 'error',
        step: 0,
        totalSteps: 4,
        message: 'Fehler bei der Generierung',
        error: (error as Error).message
      });
      throw error;
    }
  },
  
  /**
   * Save brand book to Firestore and sync to Customer Intelligence
   */
  async saveBrandBook(orgId: string, brandBook: BrandBook): Promise<string> {
    const brandBooksRef = collection(db, 'organizations', orgId, 'brandBooks');
    const docRef = doc(brandBooksRef);
    
    const brandBookWithId = {
      ...brandBook,
      id: docRef.id,
      createdAt: Timestamp.fromDate(brandBook.createdAt),
      updatedAt: Timestamp.fromDate(new Date())
    };
    
    // Remove undefined values - Firestore doesn't accept them
    const cleanedBrandBook = removeUndefinedValues(brandBookWithId);
    
    await setDoc(docRef, cleanedBrandBook);
    console.log(`✅ Brand Book saved with ID: ${docRef.id}`);
    
    // SYNC TO CUSTOMER INTELLIGENCE (360 View)
    try {
      let ci = await customerIntelligenceService.findByDomain(orgId, brandBook.domain);
      
      // If CI record doesn't exist, create one
      if (!ci) {
        console.log(`🆕 Creating new CI Record for domain: ${brandBook.domain}`);
        ci = await customerIntelligenceService.createFromUrl(orgId, brandBook.url, brandBook.createdBy);
      }

      if (ci) {
        await customerIntelligenceService.update(orgId, ci.id, {
          brand: {
            ...ci.brand,
            industry: brandBook.industry || ci.brand.industry,
            usp: brandBook.usp || ci.brand.usp,
            offerings: brandBook.offerings.length > 0 ? brandBook.offerings : ci.brand.offerings,
            targetAudience: brandBook.targetAudience || ci.brand.targetAudience,
            slogan: brandBook.slogan || ci.brand.slogan,
            industryKeywords: brandBook.industryKeywords.length > 0 ? brandBook.industryKeywords : ci.brand.industryKeywords
          },
          visual: {
            ...ci.visual,
            logoUrl: brandBook.logoUrl || ci.visual.logoUrl,
            brandColors: brandBook.brandColors.length > 0 ? brandBook.brandColors : ci.visual.brandColors,
            visualStyle: brandBook.visualStyle || ci.visual.visualStyle,
            brandImages: brandBook.brandImages.length > 0 ? brandBook.brandImages : ci.visual.brandImages
          },
          voice: {
            ...ci.voice,
            ...brandBook.voiceAnalysis
          },
          keywords: {
            ...ci.keywords,
            core: brandBook.coreKeywords.length > 0 ? brandBook.coreKeywords : ci.keywords.core,
            longTail: brandBook.longTailKeywords.length > 0 ? brandBook.longTailKeywords : ci.keywords.longTail,
            negative: brandBook.negativeKeywords.length > 0 ? brandBook.negativeKeywords : ci.keywords.negative
          },
          brandBook: {
            id: docRef.id,
            generatedAt: new Date(),
            hasGuidelines: !!brandBook.guidelines,
            visualStyle: brandBook.visualStyle,
            voiceAttributes: brandBook.voiceAnalysis.toneAttributes
          },
          updatedAt: new Date()
        });
        console.log(`🔄 Synced Brand Book data to CI Record: ${ci.id}`);
      }
    } catch (syncError) {
      console.warn('⚠️ Sync to CI failed (non-critical):', syncError);
    }
    
    return docRef.id;
  },
  
  /**
   * Get a single brand book by ID
   */
  async getBrandBook(orgId: string, brandBookId: string): Promise<BrandBook | null> {
    try {
      const docRef = doc(db, 'organizations', orgId, 'brandBooks', brandBookId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }
      
      const data = docSnap.data();
      return normalizeBrandBookDates(docSnap.id, data);
      
    } catch (error) {
      console.error('Error getting brand book:', error);
      return null;
    }
  },
  
  /**
   * Get all brand books for an organization
   */
  async getBrandBooks(orgId: string): Promise<BrandBookListItem[]> {
    try {
      const brandBooksRef = collection(db, 'organizations', orgId, 'brandBooks');
      const q = query(brandBooksRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          businessName: data.businessName,
          domain: data.domain,
          logoUrl: data.logoUrl,
          industry: data.industry,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
          linkedCompanyId: data.linkedCompanyId,
          linkedLeadId: data.linkedLeadId
        } as BrandBookListItem;
      });
      
    } catch (error) {
      console.error('Error getting brand books:', error);
      return [];
    }
  },
  
  /**
   * Update an existing brand book and sync to CI
   */
  async updateBrandBook(
    orgId: string, 
    brandBookId: string, 
    updates: Partial<BrandBook>
  ): Promise<void> {
    const docRef = doc(db, 'organizations', orgId, 'brandBooks', brandBookId);
    
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.fromDate(new Date())
    });

    // SYNC TO CUSTOMER INTELLIGENCE (360 View)
    try {
      const fullBook = await this.getBrandBook(orgId, brandBookId);
      if (fullBook) {
        let ci = await customerIntelligenceService.findByDomain(orgId, fullBook.domain);
        
        // If CI record doesn't exist, create one
        if (!ci) {
          console.log(`🆕 Creating new CI Record for domain: ${fullBook.domain}`);
          ci = await customerIntelligenceService.createFromUrl(orgId, fullBook.url, fullBook.createdBy);
        }

        if (ci) {
          await customerIntelligenceService.update(orgId, ci.id, {
            brand: {
              ...ci.brand,
              industry: fullBook.industry || ci.brand.industry,
              usp: fullBook.usp || ci.brand.usp,
              offerings: fullBook.offerings.length > 0 ? fullBook.offerings : ci.brand.offerings,
              targetAudience: fullBook.targetAudience || ci.brand.targetAudience,
              slogan: fullBook.slogan || ci.brand.slogan,
              industryKeywords: fullBook.industryKeywords.length > 0 ? fullBook.industryKeywords : ci.brand.industryKeywords
            },
            visual: {
              ...ci.visual,
              logoUrl: fullBook.logoUrl || ci.visual.logoUrl,
              brandColors: fullBook.brandColors.length > 0 ? fullBook.brandColors : ci.visual.brandColors,
              visualStyle: fullBook.visualStyle || ci.visual.visualStyle,
              brandImages: fullBook.brandImages.length > 0 ? fullBook.brandImages : ci.visual.brandImages
            },
            voice: {
              ...ci.voice,
              ...fullBook.voiceAnalysis
            },
            keywords: {
              ...ci.keywords,
              core: fullBook.coreKeywords.length > 0 ? fullBook.coreKeywords : ci.keywords.core,
              longTail: fullBook.longTailKeywords.length > 0 ? fullBook.longTailKeywords : ci.keywords.longTail,
              negative: fullBook.negativeKeywords.length > 0 ? fullBook.negativeKeywords : ci.keywords.negative
            },
            brandBook: {
              id: brandBookId,
              generatedAt: new Date(),
              hasGuidelines: !!fullBook.guidelines,
              visualStyle: fullBook.visualStyle,
              voiceAttributes: fullBook.voiceAnalysis.toneAttributes
            },
            updatedAt: new Date()
          });
          console.log(`🔄 Synced updated Brand Book data to CI Record: ${ci.id}`);
        }
      }
    } catch (syncError) {
      console.warn('⚠️ Sync to CI failed (non-critical):', syncError);
    }
  },
  
  /**
   * Delete a brand book
   */
  /**
   * Helper: Get Journey Metadata for an industry from templates
   */
  async getJourneyMetadataForIndustry(industry: string): Promise<JourneyMetdata | undefined> {
    try {
      const template = await industryTemplateService.getTemplateForIndustry(industry);
      return template?.journeyMetadata;
    } catch (error) {
      console.error('Error fetching journey metadata:', error);
      return undefined;
    }
  },

  async deleteBrandBook(orgId: string, brandBookId: string): Promise<void> {
    const docRef = doc(db, 'organizations', orgId, 'brandBooks', brandBookId);
    await deleteDoc(docRef);
  },
  
  /**
   * Get brand book by domain
   */
  async getBrandBookByDomain(orgId: string, domain: string): Promise<BrandBook | null> {
    try {
      const brandBooksRef = collection(db, 'organizations', orgId, 'brandBooks');
      const q = query(brandBooksRef, where('domain', '==', domain), limit(1));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      
      return normalizeBrandBookDates(doc.id, data);
      
    } catch (error) {
      console.error('Error getting brand book by domain:', error);
      return null;
    }
  },
  
  /**
   * Get brand book for a company
   */
  async getBrandBookForCompany(orgId: string, companyId: string): Promise<BrandBook | null> {
    try {
      const brandBooksRef = collection(db, 'organizations', orgId, 'brandBooks');
      const q = query(brandBooksRef, where('linkedCompanyId', '==', companyId), limit(1));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      
      return normalizeBrandBookDates(doc.id, data);
      
    } catch (error) {
      console.error('Error getting brand book for company:', error);
      return null;
    }
  },
  
  /**
   * Get brand book for a lead
   */
  async getBrandBookForLead(orgId: string, leadId: string): Promise<BrandBook | null> {
    try {
      const brandBooksRef = collection(db, 'organizations', orgId, 'brandBooks');
      const q = query(brandBooksRef, where('linkedLeadId', '==', leadId), limit(1));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      
      return normalizeBrandBookDates(doc.id, data);
      
    } catch (error) {
      console.error('Error getting brand book for lead:', error);
      return null;
    }
  },
  
  /**
   * Link brand book to a company
   */
  async linkToCompany(orgId: string, brandBookId: string, companyId: string): Promise<void> {
    await this.updateBrandBook(orgId, brandBookId, { linkedCompanyId: companyId });
    
    // Also update the company with the brand book reference
    const companyRef = doc(db, 'organizations', orgId, 'companies', companyId);
    await updateDoc(companyRef, { brandBookId });
  },
  
  /**
   * Link brand book to a lead
   */
  async linkToLead(orgId: string, brandBookId: string, leadId: string): Promise<void> {
    await this.updateBrandBook(orgId, brandBookId, { linkedLeadId: leadId });
    
    // Also update the lead with the brand book reference
    const leadRef = doc(db, 'organizations', orgId, 'leads', leadId);
    await updateDoc(leadRef, { brandBookId });
  },
  
  /**
   * Get context for image generation
   */
  getImageGenerationContext(brandBook: BrandBook): ImageGenContext {
    return {
      brandColors: brandBook.brandColors,
      primaryColor: brandBook.primaryColor,
      visualStyle: brandBook.visualStyle,
      imageStyle: brandBook.imageStyle,
      industry: brandBook.industry,
      businessName: brandBook.businessName
    };
  },
  
  /**
   * Get context for ad copy generation
   */
  getAdCopyContext(brandBook: BrandBook): AdCopyContext {
    return {
      voiceAnalysis: brandBook.voiceAnalysis,
      usp: brandBook.usp,
      keyBenefits: brandBook.keyBenefits,
      primaryCTA: brandBook.primaryCTA,
      targetAudience: brandBook.targetAudience,
      offerings: brandBook.offerings,
      businessName: brandBook.businessName
    };
  },
  
  /**
   * Get full brand context for AI services
   */
  getBrandContext(brandBook: BrandBook): BrandContext {
    return {
      brandBook,
      imageContext: this.getImageGenerationContext(brandBook),
      copyContext: this.getAdCopyContext(brandBook)
    };
  }
};
